import { useCallback } from 'react';
import { cn, memoClassNames } from '@/utils';
import { FlashcardSlots } from './types';
import { useFlashcardContext } from './hooks/use-flashcard-context';
import { FlashcardView } from './flashcard-view';
import { FlashcardCarousel } from './flashcard-carousel';
import { FlashcardThumbsNav } from './flashcard-thumbs-nav';
import { FlashcardToolbar } from './flashcard-toolbar';

export type FlashcardDisplayProps = React.ComponentProps<'div'> & {
  slots?: FlashcardSlots;
  title?: React.ReactNode;
};

const classes = memoClassNames('Flashcard', [
  'root',
  'flipcard',
  'nav',
  'carousel',
]);
export function FlashcardDisplay({
  className,
  slots = {},
  title,
  ...others
}: FlashcardDisplayProps) {
  const {
    Loading,
    Empty,
    Toolbar = FlashcardToolbar as FlashcardSlots['Toolbar'],
    FlipCard = FlashcardView,
    Nav = FlashcardThumbsNav,
  } = slots;

  const flashcard = useFlashcardContext();
  const { flipAll, loading, deck } = flashcard;

  const handleSettle = useCallback(() => {
    if (!flashcard.preventFlipBack) flipAll('front');
  }, [flipAll, flashcard.preventFlipBack]);

  if (loading) {
    return Loading ? <Loading /> : null;
  }

  if (!deck) {
    return Empty ? <Empty /> : null;
  }

  const slides = deck.content.map((card, i) => (
    <FlipCard
      className={classes.flipcard}
      data={card}
      disabled={i !== flashcard.displayIndex || flashcard.disabled}
      displaySide={flashcard.flipSide(i)}
      key={card.id}
      onFlip={flashcard.flip}
      preventFlipBack={flashcard.preventFlipBack}
    />
  ));

  return (
    <div
      className={cn(classes.root, 'mm:flex mm:flex-col mm:gap-4', className)}
      {...others}
    >
      {Toolbar ? <Toolbar title={deck.title} /> : null}
      <FlashcardCarousel
        className={classes.carousel}
        displayIndex={flashcard.displayIndex}
        onSettle={handleSettle}
        slides={slides}
      />

      {Nav ? (
        <Nav
          className={classes.nav}
          deck={deck}
          disabled={flashcard.disabled}
          displayIndex={flashcard.displayIndex}
          onNavigateTo={flashcard.navigateTo}
        />
      ) : null}
    </div>
  );
}

FlashcardDisplay.displayName = 'FlashcardDisplay';
